import CryptoJS from "crypto-js";
import { USER_ROLES } from "@/core/enums/USER_ROLES";
import { PERMISSIONS } from "@/core/enums/PERMISSIONS";
import { ValidationError } from "yup";
import { ELEMENT_TYPES } from "@/core/enums/ELEMENT_TYPES";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format as dateFnsFormat,
  getDay,
  isBefore,
  parse,
  addMonths,
  isValid,
} from "date-fns";
import { PhoneFieldValue } from "@/core/interfaces/profile.interface";
import { IOrganization } from "../interfaces/organization.interface";

const VITE_APP_SALT = import.meta.env.VITE_APP_SALT || "";

export const stringEncrypt = (string: string) =>
  CryptoJS.AES.encrypt(string, VITE_APP_SALT).toString();

// String decryption
export const stringDecrypt = (ciphertext: any) => {
  let value = null;
  try {
    value = CryptoJS.AES.decrypt(ciphertext, VITE_APP_SALT, {
      mode: CryptoJS.mode.CBC,
    }).toString(CryptoJS.enc.Utf8);
  } catch (_error) {
    console.error(_error);
  }
  return value;
};

export const formatNumber = (num: number | string): string => {
  let temp: any = num;
  if (typeof num === "number") {
    temp = num % 1 !== 0 ? num.toFixed(2) : num.toString();
  }
  return temp?.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
};

export function formatString(str: string, ...val: string[]) {
  for (let index = 0; index < val.length; index++) {
    str = str.replace(`{${index}}`, val[index]);
  }
  return str;
}

export function formatStringToRoutePath(
  str: string,
  values: { [key: string]: string },
) {
  for (const key in values) {
    str = str.replace(`:${key}`, values[key]);
  }
  return str;
}

export function isRoleAllowed(
  userRole: USER_ROLES,
  allowedRoles: Array<USER_ROLES>,
): boolean {
  return !allowedRoles.length || allowedRoles.includes(userRole);
}

export function isUserPermitted(
  allowedPermissions: PERMISSIONS[],
  userPermissions: PERMISSIONS[],
): boolean {
  return allowedPermissions.every((permission) => {
    return userPermissions.includes(permission);
  });
}

/** True if user has at least one of the allowed permissions (or list is empty). */
export function isUserPermittedAny(
  allowedPermissions: PERMISSIONS[],
  userPermissions: PERMISSIONS[],
): boolean {
  if (!allowedPermissions.length) return true;
  return allowedPermissions.some((permission) =>
    userPermissions.includes(permission),
  );
}

export function yupValidationErrorAsSchema(
  validationError: ValidationError,
): any {
  return validationError.inner.reduce(
    (accumulator, error) => ({
      ...accumulator,
      ...(error.path && { [error.path]: error.message }),
    }),
    {},
  );
}

export const genTestId = (type: ELEMENT_TYPES, name: string) => {
  return `${type}-${name}`;
};

export const getCurrencyFormatter = (currency = "PKR") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export const getFormattedDateTime = (timeStamp: Date, format: string) => {
  return dateFnsFormat(new Date(timeStamp), format);
};

export const minPassportExpiry = (): Date => {
  const date = addMonths(new Date(), 6);
  date.setDate(date.getDate() + 1);
  return date;
};

export const toDateFromDDMMYYYY = (dateString: string): Date | null => {
  const parts = dateString.split("-");
  let YYYYMMDDDateString: string | Date = "";

  if (parts.length === 3) {
    if (
      parts[0].length === 2 &&
      parts[1].length === 2 &&
      parts[2].length === 4
    ) {
      YYYYMMDDDateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }

  const isoDate = parse(YYYYMMDDDateString, "yyyy-MM-dd", new Date());

  if (isValid(isoDate)) {
    return isoDate;
  }

  return null;
};

export const getDayFromDate = (timeStamp: Date) => {
  return getDay(new Date(timeStamp));
};

export const isDateExpired = (from: Date, to?: Date): boolean => {
  to = to || new Date();
  return isBefore(from, to || new Date(Date.now()));
};

export const validateEmail = (email: string) => {
  const emailRegex = new RegExp(
    "[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?",
  );
  return emailRegex.test(email);
};

export const validateName = (name: string) => {
  if (name === null || name.trim().length < 1) {
    return "Name is required";
  } else {
    const regex = /^[^\d@]+(\s+[^\d@]+)*$/;
    const testName = regex.test(name.trim());
    return testName === false ? "Name is Invalid" : "";
  }
};
export const validateMobileNumber = (mobileNumber: string) => {
  const regex = /^03\d{9}$/;
  return regex.test(mobileNumber);
};

export const validateRequiredField = (
  value: string | number | Date | PhoneFieldValue,
  displayName: string,
): string | null => {
  if (
    (!value && value !== "0") ||
    (typeof value === "string" && !value.trim()) ||
    (typeof value === "number" && isNaN(value))
  ) {
    return `${displayName} is required`;
  }
  return null;
};

export const validateMaxLength = (
  value: string,
  maxLength: number,
  displayName: string,
): string | null => {
  if (value.length > maxLength) {
    return `${displayName} cannot exceed ${maxLength} characters`;
  }
  return null;
};

export const validateNumber = (
  value: string,
  displayName: string,
  allowNegative = false,
): string | null => {
  if (isNaN(Number(value))) {
    return `${displayName} must be a valid number`;
  }
  if (!allowNegative && Number(value) < 0) {
    return `${displayName} cannot be negative`;
  }
  return null;
};

export const getDurationText = (from?: Date, to?: Date): string => {
  const _from = from || new Date(Date.now());
  const _to = to || new Date(Date.now());
  const minutesDiff = differenceInMinutes(_to, _from);
  const hoursDiff = differenceInHours(_to, _from);

  const daysDiff = differenceInDays(_to, _from);
  const remainingMinutes = minutesDiff % 60;
  const remainingHours = hoursDiff % 24;

  const duration = {
    days: daysDiff,
    minutes: remainingMinutes,
    hours: remainingHours,
  };

  let durationText = "";
  durationText += duration.days
    ? `${duration.days} ${duration.days > 1 ? "days" : "day"} `
    : "";
  durationText += duration.hours
    ? `${duration.hours} ${duration.hours > 1 ? "hrs" : "hr"} `
    : "";
  durationText += duration.hours
    ? `${duration.minutes} ${duration.minutes > 1 ? "mins" : "min"} `
    : "";

  return durationText;
};

export const formatQueryPath = (obj: any) => {
  let path = "";
  Object.keys(obj).forEach((item, index) => {
    const prefix = index === 0 ? `?` : `&`;
    path += `${prefix}${item}=${obj[item]}`;
  });
  return path;
};

export const formatImageUrl = (url: string) => {
  const firstDoubleSlashIndex = url.indexOf("//");
  return (
    url.slice(0, firstDoubleSlashIndex + 2) +
    url.slice(firstDoubleSlashIndex + 2).replace(/\/\//g, "/")
  );
};

export const formatWordIntoCapitalize = (str: string) =>
  str.toLowerCase().replace(/(?:^|\s|[-"'([{])+\S/g, (c) => c.toUpperCase());

export const hotelNumberOfNightStayText = (
  _checkInDate: Date,
  _checkOutDate: Date,
) => {
  const daysDiff = differenceInDays(_checkOutDate, _checkInDate);

  return `${daysDiff} NIGHT(S) STAY`;
};

export const parseIntoCurrentDateIgnoringTimezone = (date: string | Date) => {
  if (date instanceof Date) {
    return date;
  }

  if (!date) return new Date(NaN);

  const trimmed = date.trim();

  // If the date is e.g. 2026-09-19, then we need to parse it as a date only string
  // Otherwise, it will get localized to the user's timezone
  const isDateOnlyString = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  if (isDateOnlyString) {
    const parsedDateOnly = parse(trimmed, "yyyy-MM-dd", new Date());
    return isValid(parsedDateOnly) ? parsedDateOnly : new Date(NaN);
  }

  const dateWithTimezoneStripped = trimmed.replace(/[+-]\d{2}:\d{2}$/, "");

  const parsedDateTime = parse(
    dateWithTimezoneStripped,
    "yyyy-MM-dd'T'HH:mm:ss",
    new Date(),
  );

  return isValid(parsedDateTime) ? parsedDateTime : new Date(NaN);
};

export const clearCache = () => {
  caches.keys().then((keyList) =>
    Promise.all(
      keyList.map((key) => {
        return caches.delete(key);
      }),
    ),
  );
};

export const getGenderByTitleValue = (titleValue?: string): string => {
  switch (titleValue) {
    case "MR":
    case "MASTER":
      return "M";

    case "MS":
    case "MRS":
      return "F";

    case "O":
      return "O";

    default:
      return "";
  }
};

export const copyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

export const sanitizeNumberInput = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

export const sanitizeStringInput = (value: string) => {
  return value.trim().replaceAll("_", " ");
};

export const cleanOrganizationSectors = (
  organizations: IOrganization[],
): IOrganization[] => {
  if (!Array.isArray(organizations)) return [];

  return organizations.map((org) => {
    const cleanedSector = org?.sector
      ?.replace(/^Quality Aviation\s*-\s*/i, "")
      ?.replace(/^Aeroglobe\s*-\s*/i, "")
      ?.trim();

    return {
      ...org,
      sector: cleanedSector || org.sector,
    };
  });
};

export const waitForMs = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const parseCommaSeparatedParams = (value?: string | null): string[] => {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const safeParseInteger = (
  value: string | null,
  fallback = 0,
): number => {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};
